import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { UserAccount } from '../types/user';
import UsageTrackingService from './usageTrackingService';

export class UserService {
  /**
   * Admin emails that should have premium access
   */
  private static readonly ADMIN_EMAILS = [
    'xuwei@indexalpha.ai',
    'jerry@indexalpha.ai'
  ];

  /**
   * Check if email should have premium access
   */
  private static isPremiumEmail(email: string | null): boolean {
    if (!email) return false;
    return this.ADMIN_EMAILS.includes(email.toLowerCase());
  }

  /**
   * Get or create user document in Firestore
   */
  static async getOrCreateUser(firebaseUser: User): Promise<UserAccount | null> {
    if (!firebaseUser) return null;

    console.log('UserService.getOrCreateUser called for:', firebaseUser.email);
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const isPremiumUser = this.isPremiumEmail(firebaseUser.email);

    try {
      console.log('Attempting to get document...');
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        // User exists, return the data
        const data = docSnap.data();

        // Update object for batch updates
        const updates: any = {};

        // Update avatarUrl if it's not set but photoURL is available
        if (!data.avatarUrl && firebaseUser.photoURL) {
          updates.avatarUrl = firebaseUser.photoURL;
        }

        // Auto-upgrade admin emails to premium if they're not already
        if (isPremiumUser && data.subscriptionPlan !== 'premium') {
          console.log(`🎯 Auto-upgrading ${firebaseUser.email} to premium plan`);
          updates.subscriptionPlan = 'premium';
          updates.subscriptionStatus = 'active';
          updates.subscriptionStartDate = serverTimestamp();
        }

        // Apply updates if any
        if (Object.keys(updates).length > 0) {
          updates.updatedAt = serverTimestamp();
          await updateDoc(userDocRef, updates);

          // Re-fetch the document to get the actual server timestamp values
          const updatedDoc = await getDoc(userDocRef);
          if (updatedDoc.exists()) {
            const updatedData = updatedDoc.data();
            return {
              uid: updatedDoc.id,
              ...updatedData,
              avatarUrl: updatedData.avatarUrl || firebaseUser.photoURL || undefined,
              createdAt: updatedData.createdAt?.toDate() || new Date(),
              updatedAt: updatedData.updatedAt?.toDate() || new Date(),
              avatarGeneratedAt: updatedData.avatarGeneratedAt?.toDate()
            } as UserAccount;
          }
        }

        return {
          uid: docSnap.id,
          ...data,
          avatarUrl: data.avatarUrl || firebaseUser.photoURL || undefined,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          avatarGeneratedAt: data.avatarGeneratedAt?.toDate()
        } as UserAccount;
      } else {
        // User doesn't exist, create new document
        const newUserData: Partial<UserAccount> = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || '',
          onboardingComplete: true,
          interests: [],
          watchlist: [],
          preferences: {
            emailNotifications: true,
            dailyDigest: true,
            marketAlerts: true,
            reportFrequency: 'daily',
            theme: 'light'
          }
        };
        // Only attach avatarUrl when the provider supplied one. OAuth
        // accounts without a photo (email/password, some Twitter) used
        // to write `avatarUrl: undefined` which broke the entire new-
        // user doc write under the Firestore Web SDK.
        if (firebaseUser.photoURL) {
          newUserData.avatarUrl = firebaseUser.photoURL;
        }

        if (isPremiumUser) {
          console.log(`🎯 Creating new premium account for ${firebaseUser.email}`);
        }

        // Use serverTimestamp for consistency
        await setDoc(userDocRef, {
          ...newUserData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        console.log('User document created successfully for:', firebaseUser.uid);

        // Initialize 3-month trial for new users (unless they're premium)
        if (!isPremiumUser) {
          console.log('🎁 Initializing 3-month trial for:', firebaseUser.email);
          try {
            await UsageTrackingService.initializeTrial(firebaseUser.uid);
            console.log('✅ Trial initialized successfully');
          } catch (error) {
            console.error('⚠️ Failed to initialize trial:', error);
            // Don't fail user creation if trial initialization fails
          }
        }

        // Return the created user with client-side dates
        return {
          ...newUserData,
          createdAt: new Date(),
          updatedAt: new Date()
        } as UserAccount;
      }
    } catch (error: any) {
      console.error('Error in getOrCreateUser:', error);
      
      // Handle specific Firestore errors
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access this resource. Please contact support.');
      } else if (error.code === 'unavailable') {
        throw new Error('Service temporarily unavailable. Please try again later.');
      } else {
        throw new Error(`Failed to load user data: ${error.message}`);
      }
    }
  }

  /**
   * Update user document
   */
  static async updateUser(userId: string, data: Partial<UserAccount>): Promise<void> {
    const userDocRef = doc(db, 'users', userId);
    
    try {
      await updateDoc(userDocRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update this profile.');
      } else {
        throw new Error(`Failed to update profile: ${error.message}`);
      }
    }
  }

}
